import { Circle, FabricText, Group, Line } from 'fabric';
import type { FabricObject } from 'fabric';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ContentSourceSpec, StandardContentPayload, ToolRecipe } from '../types';

/**
 * Primary handwriting practice lines — the ruled guides early-elementary
 * teachers use for name tracing and letter formation: solid top line, DASHED
 * midline, heavier baseline, optional descender line, with traceable or model
 * words sitting on the baseline.
 *
 * THE CRAFT — why the ruling is measured, not hard-coded.
 * On a real worksheet, tall letters (b d h k l, and the capitals) touch the
 * TOP line and short letters (a c e o x) touch the MIDLINE. That relationship
 * is a property of the FONT: the midline must land at the font's x-height and
 * the top line at its ascender height. Handwriting models (Zaner-Bloser and
 * friends) put the midline near 50-58% of the way down from top line to
 * baseline because their x-height is ~45% of their ascender height; ordinary
 * text faces run nearer 70%, which would put the midline higher. Pinning the
 * ruling to a fixed percentage would therefore leave the letters floating off
 * the midline in every font that isn't the one the percentage was tuned for —
 * the classic broken-worksheet look. So we PROBE the actual face (canvas
 * TextMetrics on flat-topped glyphs) and derive:
 *
 *     fontSize   = rowHeight / ascender          (ascenders exactly fill
 *                                                 top line → baseline)
 *     midlineY   = baselineY - rowHeight * (xHeight / ascender)
 *     descenderY = baselineY + rowHeight * (descender / ascender)
 *
 * The letters then sit on the baseline and hit the midline for ANY font the
 * teacher picks. The default face (Patrick Hand) is a bundled handwriting
 * design whose natural ratio lands the midline right in the primary-ruling
 * band, so the stock object looks like classroom paper out of the box.
 *
 * Emits only stock primitives (Line / FabricText / Circle in a Group) tagged
 * data:{tool,version,params} — survives save/reload as plain fabric JSON.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion — params arrive untrusted (user- or LLM-typed, or
// replayed from stored JSON on re-edit); generate() must never throw.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value here (= blank lines). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Fabric text baseline placement
// ---------------------------------------------------------------------------

/**
 * Fabric's own vertical text layout constants (Text/constants.mjs). For a
 * SINGLE-line FabricText with uniform styling, fabric renders with
 * ctx.textBaseline='alphabetic' at:
 *
 *   height          = fontSize * _fontSizeMult            (calcTextHeight uses
 *                                                          getHeightOfLineImpl
 *                                                          for the last line,
 *                                                          so lineHeight drops
 *                                                          out entirely)
 *   baseline - top  = height/2 + height*(0.5 - _fontSizeFraction)
 *                   = fontSize * _fontSizeMult * (1 - _fontSizeFraction)
 *
 * Note this offset depends only on fontSize — NOT on the font face — so with
 * originY:'top' we can drop the alphabetic baseline exactly on our ruled
 * baseline for any family. One FabricText per row keeps the single-line form
 * (a multi-line text would reintroduce lineHeight).
 */
const FABRIC_FONT_SIZE_MULT = 1.13;
const FABRIC_FONT_SIZE_FRACTION = 0.222;
const BASELINE_FROM_TOP = FABRIC_FONT_SIZE_MULT * (1 - FABRIC_FONT_SIZE_FRACTION);

// ---------------------------------------------------------------------------
// Font metrics probe
// ---------------------------------------------------------------------------

interface FontMetrics {
  /** Tallest ascender/cap above the baseline, per em — the TOP line. */
  ascender: number;
  /** Flat x-height above the baseline, per em — the MIDLINE. */
  xHeight: number;
  /** Deepest descender below the baseline, per em. */
  descender: number;
}

/** Sane humanist-sans ratios — used when TextMetrics is unavailable or absurd. */
const METRICS_FALLBACK: FontMetrics = { ascender: 0.73, xHeight: 0.52, descender: 0.21 };

/** Big probe em keeps sub-pixel rounding out of the derived ratios. */
const PROBE_EM = 200;

/** Caps AND tall lowercase both reach the top line on a worksheet. */
const ASCENDER_PROBES = ['b', 'd', 'h', 'k', 'l', 'H', 'E'];
/** Flat-topped only — round letters (o, c, e) overshoot the x-height. */
const X_HEIGHT_PROBES = ['x', 'z', 'v', 'w'];
const DESCENDER_PROBES = ['g', 'j', 'p', 'q', 'y'];

const METRICS_CACHE = new Map<string, FontMetrics>();

function maxAscent(ctx: CanvasRenderingContext2D, glyphs: string[]): number {
  let best = 0;
  for (const g of glyphs) {
    const a = ctx.measureText(g).actualBoundingBoxAscent;
    if (Number.isFinite(a) && a > best) best = a;
  }
  return best;
}

function maxDescent(ctx: CanvasRenderingContext2D, glyphs: string[]): number {
  let best = 0;
  for (const g of glyphs) {
    const d = ctx.measureText(g).actualBoundingBoxDescent;
    if (Number.isFinite(d) && d > best) best = d;
  }
  return best;
}

/** Measure the real face; null when the environment or the numbers are unusable. */
function probeMetrics(family: string): FontMetrics | null {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;
    ctx.font = `${PROBE_EM}px "${family}", sans-serif`;
    const ascender = maxAscent(ctx, ASCENDER_PROBES);
    const xHeight = maxAscent(ctx, X_HEIGHT_PROBES);
    const descender = maxDescent(ctx, DESCENDER_PROBES);
    // A face whose x-height is not strictly inside the ascender height is a
    // failed measurement (or an all-caps/display face) — the ruling would be
    // nonsense, so fall back rather than draw a midline through the letters.
    if (!(ascender > 0) || !(xHeight > 0) || xHeight >= ascender * 0.95) return null;
    return {
      ascender: ascender / PROBE_EM,
      xHeight: xHeight / PROBE_EM,
      descender: descender > 0 ? descender / PROBE_EM : METRICS_FALLBACK.descender,
    };
  } catch {
    return null;
  }
}

/**
 * Metrics for `family`, loading the face first. Canvas text does NOT trigger
 * font loading by itself — probing an unloaded family would silently measure
 * the fallback and rule the whole sheet to the wrong font.
 */
async function fontMetrics(family: string): Promise<FontMetrics> {
  const cached = METRICS_CACHE.get(family);
  if (cached) return cached;
  try {
    if (typeof document !== 'undefined' && document.fonts) {
      await document.fonts.load(`${PROBE_EM}px "${family}"`);
    }
  } catch {
    /* no font-loading API — measure whatever the browser resolves. */
  }
  const measured = probeMetrics(family);
  if (!measured) return METRICS_FALLBACK;
  // Only cache once the face is CONFIRMED present. An unregistered family
  // still measures cleanly, but those are the FALLBACK's metrics: caching them
  // would rule every later sheet in this family to the wrong font, even after
  // the real face lands. Uncached, the next regenerate re-measures. (Both the
  // insert and Apply paths ensureFont the recipe's families first, so this is
  // the safety net, not the common case.)
  let available = true;
  try {
    if (typeof document !== 'undefined' && document.fonts) {
      available = document.fonts.check(`${PROBE_EM}px "${family}"`);
    }
  } catch {
    available = false;
  }
  if (available) METRICS_CACHE.set(family, measured);
  return measured;
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

/**
 * The face the ruling is drawn for. A worksheet font is judged on TWO numbers:
 *
 *   1. x-height / ascender — where this recipe puts the dashed midline (see the
 *      geometry note above). Classic Zaner-Bloser primary ruling puts the
 *      midline halfway up the row, i.e. ~50%; the usable band is 45-55%.
 *   2. SLANT — US manuscript instruction (Zaner-Bloser ball-and-stick) is
 *      UPRIGHT; the slanted D'Nealian style is the US minority, and the AU
 *      school scripts are slanted throughout. Our users are mostly US teachers.
 *
 * Both measured from the ink, not eyeballed: ratio with this file's own probe,
 * slant by rendering l/H/I at 400px and comparing the ink centroid of the top
 * decile of rows against the bottom decile (+ = leans right).
 *
 *   font                     slant    x-h/asc
 *   Edu NSW ACT Foundation   +6.0deg   54.2%   <- bundled, DEFAULT
 *   Edu SA Beginner          +6.1deg   54.2%
 *   Edu AU VIC WA NT Pre     +9.8deg   54.8%
 *   Edu VIC WA NT Beginner   +9.9deg   54.8%
 *   Edu QLD Beginner        +10.1deg   54.2%   <- bundled, AU/D'Nealian slant
 *   Edu TAS Beginner        +13.9deg   54.2%
 *   Schoolbell               +2.6deg   59.4%
 *   Patrick Hand             +0.9deg   65.0%   <- old default: midline rode up
 *   Andika                   +0.0deg   62.9%   <- bundled, UPRIGHT option
 *   Comic Neue               -0.0deg   72.3%
 *   ABeeZee                  +0.0deg   72.3%
 *   Atkinson Hyperlegible    -0.0deg   73.9%
 *   Arial                    -0.0deg   73.8%
 *
 * The two goals are mutually exclusive in anything OFL we could find: the only
 * faces DRAWN for handwriting instruction (single-storey a/g, entry strokes,
 * ~54% ratio) are the AU "Edu" family, and every one of them is slanted. Every
 * genuinely upright face is an ordinary text/literacy face whose x-height is
 * 63-74% — too tall for classic ruling.
 *
 * Ratio wins over slant for the DEFAULT, because the ratio is the thing this
 * recipe actually draws: at 62.9% the midline sits 63% of the way up the row
 * instead of ~50% and stops reading as primary ruling (exactly why Patrick Hand
 * was dropped). So the default is an in-band face — and among the in-band
 * faces, the LEAST SLANTED one, since US manuscript instruction is upright and
 * our users are mostly US teachers. That is Edu NSW ACT Foundation: same 54.2%
 * ratio as Edu QLD (so the ruling is identical) at +6.0deg instead of +10.1deg.
 * Slant is a free win here; it cost the ruling nothing.
 *
 * The other two stay bundled because "upright" is a district policy, not a
 * universal: Edu QLD for AU / D'Nealian-style districts that want the stronger
 * slant, and Andika (SIL, drawn for beginning readers) for a strict
 * Zaner-Bloser district that wants a true 0deg face and will accept the high
 * midline. Nothing is locked — the ruling re-measures whatever face the teacher
 * picks, so every one of them rules correctly.
 */
const DEFAULT_FONT = 'Edu NSW ACT Foundation';

const LINE_COLOR = '#64748b';
const MIDLINE_COLOR = '#94a3b8';
/** Light enough to trace/write over, dark enough to read. */
const TEXT_COLOR = '#a3aab7';
/** The classic "start here" green. */
const START_COLOR = '#22c55e';

// ---------------------------------------------------------------------------
// Smart-fill seam — populate the practice words from a standard's vocabulary.
// ---------------------------------------------------------------------------

/** Keep pathological graph payloads from exploding the sheet. */
const MAX_TERMS = 24;

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['vocabulary'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge into a COPY of current so every styling/layout param (rows,
    // style, showMidline/showDescender/startDot, fonts, colors, rowHeight,
    // width) survives untouched — a fill changes WHAT is practiced, never
    // HOW. `rows` in particular stays a teacher choice on purpose: it is the
    // "practice rows" ruling count, not content, so it is left alone here
    // even when the term count doesn't divide it evenly (generate() shows
    // words[r % words.length] per row — a fill with more terms than rows
    // simply prints the first `rows` terms; the rest are still saved in
    // `text` for a teacher who bumps rows up).
    const next = { ...current };
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];
    const terms = vocab
      .map((v) => (typeof v?.term === 'string' ? v.term.trim() : ''))
      .filter((t) => t.length > 0);
    // `text` reads one word per LINE (rawStr(params.text,...).split('\n')) —
    // NOT an array — so the fill must join with '\n', matching generate()'s
    // parse exactly (word-wall's mistake to avoid: mirror the reader, don't
    // guess the shape).
    if (terms.length) {
      next.text = terms.slice(0, MAX_TERMS).join('\n');
    }
    // No title/heading field on this recipe to gate (`text` IS the content).
    // Provenance (survives regenerate — declared schema fields below).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const handwritingLines: ToolRecipe = {
  id: 'handwriting-lines',
  domain: 'ela',
  tags: ['handwriting', 'writing', 'tracing', 'penmanship', 'primary-lines', 'K-2'],
  featured: true,
  name: 'Handwriting Lines',
  // The sidebar only surfaces chart/math/decor accordions; 'math' is where the
  // other classroom manipulatives (number line, ten frame, clock) live, so it
  // is the discoverable home for a teaching tool until a literacy category
  // exists.
  category: 'math',
  version: 1,
  description:
    'Primary handwriting practice rows — solid top line, dashed midline, heavy baseline and optional descender line, with traceable or model words sitting on the baseline.',
  defaults: {
    text: 'Hello',
    rows: 3,
    style: 'trace',
    showMidline: true,
    showDescender: true,
    startDot: false,
    fontFamily: DEFAULT_FONT,
    textColor: TEXT_COLOR,
    lineColor: LINE_COLOR,
    midlineColor: MIDLINE_COLOR,
    rowHeight: 110,
    width: 900,
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    {
      key: 'text',
      label: 'Words to practice',
      type: 'multiline',
      help: 'One word or name per line. A single line repeats on every row; leave empty for blank practice lines.',
    },
    { key: 'rows', label: 'Practice rows', type: 'number', min: 1, max: 12, step: 1 },
    {
      key: 'style',
      label: 'Letter style',
      type: 'select',
      options: [
        { value: 'trace', label: 'Trace (dotted outlines)' },
        { value: 'solid', label: 'Model (solid letters)' },
        { value: 'blank', label: 'Blank (lines only)' },
      ],
    },
    {
      key: 'showMidline',
      label: 'Dashed midline',
      type: 'boolean',
      section: 'Guides',
      help: 'The dashed guide short letters reach up to.',
    },
    {
      key: 'showDescender',
      label: 'Descender line',
      type: 'boolean',
      section: 'Guides',
      help: 'Adds the lower line for tails like g, p and y (4-line ruling instead of 3).',
    },
    {
      key: 'startDot',
      label: 'Start dot',
      type: 'boolean',
      section: 'Guides',
      help: 'Green dot marking where to begin writing.',
    },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Typography' },
    { key: 'textColor', label: 'Letter color', type: 'color', section: 'Typography' },
    { key: 'lineColor', label: 'Line color', type: 'color', section: 'Colors' },
    { key: 'midlineColor', label: 'Midline color', type: 'color', section: 'Colors' },
    {
      key: 'rowHeight',
      label: 'Line size (px)',
      type: 'number',
      min: 40,
      max: 400,
      step: 5,
      section: 'Layout',
      help: 'Height from the top line down to the baseline. Bigger = easier for little hands.',
    },
    {
      key: 'width',
      label: 'Width (px)',
      type: 'number',
      min: 200,
      max: 4000,
      step: 10,
      advanced: true,
    },
    {
      key: 'sourceStandardCode',
      label: 'Source standard code',
      type: 'text',
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
    {
      key: 'sourceStandardId',
      label: 'Source standard id',
      type: 'text',
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
  ],
  contentSource,
  async generate(params) {
    const rows = clamp(Math.round(num(params.rows, 3)), 1, 12);
    const style = str(params.style, 'trace');
    const showMidline = bool(params.showMidline, true);
    const showDescender = bool(params.showDescender, true);
    const startDot = bool(params.startDot, false);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const textColor = str(params.textColor, TEXT_COLOR);
    const lineColor = str(params.lineColor, LINE_COLOR);
    const midlineColor = str(params.midlineColor, MIDLINE_COLOR);
    const rowHeight = clamp(num(params.rowHeight, 110), 20, 600);
    const width = clamp(num(params.width, 900), 120, 5000);

    // One word/name per line; a single line is the name-tracing case and
    // repeats down the sheet. 'blank' drops the text but keeps the ruling.
    const words =
      style === 'blank'
        ? []
        : rawStr(params.text, 'Hello')
            .split('\n')
            .map((w) => w.trim())
            .filter((w) => w.length > 0);

    const metrics = await fontMetrics(family);
    // Ratios relative to the WRITING HEIGHT (top line → baseline), which is
    // what rowHeight names. See the header comment for why these are measured.
    const xRatio = metrics.xHeight / metrics.ascender;
    const descRatio = metrics.descender / metrics.ascender;

    const fontSize = rowHeight / metrics.ascender;
    const descPx = rowHeight * descRatio;
    // Descenders need their room whether or not the line is drawn, otherwise a
    // 3-line sheet collides 'g' tails into the next row's top line. The gap
    // must stay comfortably WIDER than the descender space: when the two are
    // similar the descender line floats midway between its baseline and the
    // next top line, the rows stop reading as rows, and the sheet degrades
    // into an undifferentiated stack of lines.
    const gap = rowHeight * 0.65;
    const pitch = rowHeight + descPx + gap;

    const padX = Math.max(20, rowHeight * 0.45);

    // Stroke weights + dash lengths scale with the ruling so the sheet reads
    // the same at any line size. The baseline is deliberately the heaviest
    // line — it is the one children are taught to sit letters on.
    const baseW = Math.max(2, rowHeight * 0.03);
    const topW = Math.max(1.2, rowHeight * 0.018);
    const midW = Math.max(1, rowHeight * 0.016);
    const descW = Math.max(0.8, rowHeight * 0.013);

    const children: FabricObject[] = [];

    const rule = (
      y: number,
      stroke: string,
      strokeWidth: number,
      dash?: number[],
    ): Line =>
      new Line([0, y, width, y], {
        stroke,
        strokeWidth,
        strokeLineCap: 'butt',
        ...(dash ? { strokeDashArray: dash } : {}),
      });

    for (let r = 0; r < rows; r++) {
      const baselineY = r * pitch + rowHeight;
      const topY = baselineY - rowHeight;

      children.push(rule(topY, lineColor, topW));
      if (showMidline) {
        children.push(
          rule(baselineY - rowHeight * xRatio, midlineColor, midW, [
            rowHeight * 0.055,
            rowHeight * 0.045,
          ]),
        );
      }
      if (showDescender) {
        children.push(
          rule(baselineY + descPx, midlineColor, descW, [rowHeight * 0.035, rowHeight * 0.05]),
        );
      }
      // Baseline last so it paints over the dashes where they meet the ends.
      children.push(rule(baselineY, lineColor, baseW));

      const word = words.length > 0 ? words[r % words.length] : '';
      if (word) {
        children.push(
          new FabricText(word, {
            left: padX,
            // Drop fabric's alphabetic baseline exactly on the ruled baseline.
            top: baselineY - fontSize * BASELINE_FROM_TOP,
            originX: 'left',
            originY: 'top',
            fontFamily: family,
            fontSize,
            // Trace letters are hollow outlines drawn in a fine dash; the
            // solid model is a normal filled word.
            fill: style === 'trace' ? '' : textColor,
            ...(style === 'trace'
              ? {
                  stroke: textColor,
                  strokeWidth: Math.max(0.8, fontSize * 0.022),
                  strokeDashArray: [fontSize * 0.055, fontSize * 0.045],
                  strokeLineCap: 'round',
                }
              : {}),
          }),
        );
      }

      if (startDot) {
        // Sits ON the first letter's top-left corner — where the pencil
        // actually goes down. Offsetting it clear of the text instead reads
        // as a stray bullet rather than a start marker.
        children.push(
          new Circle({
            left: padX,
            top: topY,
            originX: 'center',
            originY: 'center',
            radius: Math.max(2, rowHeight * 0.05),
            fill: START_COLOR,
          }),
        );
      }
    }

    // Children carry absolute coords; Group derives the bbox and the caller
    // places it (mirrors every other recipe).
    const group = new Group(children);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, handwritingLines, params);
  },
};

registerRecipe(handwritingLines);
